import { BCMSDefaultModel, BCMSRecord, BCMSStateManagerCRUD, DisabledPrompt, Link, StateManager, useBCMSContext, useRoute, useStateManager, useWorkflow, useWorkflowStep } from "@bcm-dev/bcms-vite";
import AddCardWrapper from "components/shared/containers/AddCardWrapper";
import MainContentContainer, { MainContentHeader } from "components/shared/containers/MainContentContainer";
import { UICancelButton, UIPrimaryButton } from "components/ui/UIButton";
import UITextInput from "components/ui/UITextInput";
import { getDefaultCard, getWorkflowSteps } from "components/workflows/AddCard";
import Classes, { Card } from "model";
import React from "react";
import "./styles.css";

/**
 * @function AddCardPage
 * @return {React.Component}
 * @exported true
 * @description The main component for AddCardPage
 */

export default function AddCardPage(props) {
  const context = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, context);
  const defaultData = getDefaultData(context);
  const { goStep, state } = useWorkflow() ?? {};
  const navigate = useRoute();
  const data = { ...getDefaultData(context), ...state, ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };

  // getCardNumber :: (OBJECT, STRING|VOID) -> VOID
  // Returns a validated card number. 
  // "cardType" is an optional argument if you know which type of card you want to use.
  function getCardNumber(_currState, cardType) {
    var card = _currState.data.card;
    const options = _currState.data.branding.options;
    let defaultCard = options.include_debit_chw23 === "false"
    ? BCMSDefaultModel.creditCard()
    : BCMSDefaultModel.debitCard();
    
    if(cardType) {
      if(cardType === "debit") {
        defaultCard = BCMSDefaultModel.debitCard();
      } else if(cardType === "credit") {
        defaultCard = BCMSDefaultModel.creditCard()
      }
    }

    var number = card.number === undefined || card.number.length < 4 ? defaultCard.number : card.number;
    return number;
  }

  const controller = (state, setState) => ({
    data: state.data,
    ui: state.ui,

    update: (obj, prop) => val => {
      const crud = BCMSStateManagerCRUD.init(state);
      crud.update([ "data", obj, prop ], val);
      crud.submit(setState);
    },

    goToUserVerificationPage: () => {
      const lostOrStolenDebitOption = state.data.branding.options.loststolen_debit_chw23;
      const lostOrStolenCreditOption = state.data.branding.options.loststolen_credit_chw23;
      const hasSelfServiceDigitalIssuanceBrandingOn = lostOrStolenDebitOption === "self-service with digital issuance"
       || lostOrStolenCreditOption === "self-service with digital issuance";

      const crud = BCMSStateManagerCRUD.init(state);
      const hasSelfServiceDigitalIssuanceDebitBrandingOn = lostOrStolenDebitOption === "self-service with digital issuance";
      const useCardType = hasSelfServiceDigitalIssuanceDebitBrandingOn ? "debit" : "credit";

      var number = getCardNumber(state, useCardType);
      crud.update([ "data", "card", "number" ], number);

      if(hasSelfServiceDigitalIssuanceBrandingOn) {
        crud.update([ "data", "card", "type" ], useCardType);
      }

      crud.submit(setState);
      goStep(2, { data, ui });
    },

    cancel: () => {
      navigate(`/home/recenttransactions/`);
    },

    next: () => {
      var number = getCardNumber(state);
      const crud = BCMSStateManagerCRUD.init(state);
      crud.update([ "data", "card", "number" ], number);
      crud.submit(setState);
      goStep(1, { data, ui });
    },
  });

  return (
    <StateManager state={ { data, ui } } controller={ controller }>
      <PageContent/>
    </StateManager>
  );
}

export const getDefaultData = controller => {
  const branding = controller.state.branding;
  const options = controller.state.branding.options;
  const card = options.include_debit_chw23 === "false"
      ? BCMSDefaultModel.creditCard()
      : BCMSDefaultModel.debitCard();
  card.number = "";
  card.expDatePlaceholder = "";
  card.id = Card.nextID();

  if(options.activate_card_chw23 === "false") {
      card.active = true;
  }

  return { 
    card,
    branding
  };
}

export const getDefaultUI = () => {

}

function PageContent(props) {
  const { data, update, cancel, next, goToUserVerificationPage } = useStateManager();
  const { card } = data;
  const { colors, options } = data.branding;
  const workflowStep = useWorkflowStep(getDefaultCard, getWorkflowSteps);

  const debitImage = colors.dc_bgimage_chw23;
  const creditImage = colors.cc_bgimage_chw23;
  const styles = { "backgroundImage": `url(${ card.type === "debit" ? debitImage : creditImage })` }
  
  const lostOrStolenDebitOption = options.loststolen_debit_chw23;
  const lostOrStolenCreditOption = options.loststolen_credit_chw23;

  const hasSelfServiceDigitalIssuanceBrandingOn = lostOrStolenDebitOption === "self-service with digital issuance"
  || lostOrStolenCreditOption === "self-service with digital issuance";

  return (
    <AddCardWrapper className="add-card-page">
      <MainContentContainer header={<MainContentHeader text="Add Card" icon="LeftChevronIcon" />} >
        <div className="flex-container flex-vertical all-center-container add-card-content-container">
          <p className="text-center">Enter your card number to begin registration.</p>

          <div className="card-image flex-container all-center-container" style={ styles }>
            <UITextInput type="password" placeholder="Card Number*" value={ card.number } onChange={ update("card", "number") } dataPoint="Add_Card_Number_Field" />
          </div>

         { hasSelfServiceDigitalIssuanceBrandingOn &&
          <Link className="access-code-text" onClick={ goToUserVerificationPage }>
            <b data-point="Add_Card_Access_Code">I have an access code</b>
          </Link>
         }

          <div className="button-container flex-container flex-vertical all-center-container">
            <UIPrimaryButton text="Next" onClick={ next } dataPoint="Add_Card_Next" />
            <UICancelButton text="Cancel" onClick={ cancel } />
          </div>
          

          <p className="text-center bottom-border bottom-text">Your card number is used to verify service eligibility. When the card number has been verified, you will be asked to provide additional information to verify your identity.</p>

          <p className="text-center bottom-text">By signing in to the application, you agree to the <DisabledPrompt><Link>Terms of Conditions</Link></DisabledPrompt> and <DisabledPrompt><Link>Privacy Policy.</Link></DisabledPrompt></p>
        </div>
      </MainContentContainer>
    </AddCardWrapper>
  );
}